import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import Swal from 'sweetalert2';
import DocViewModal from '../components/DocViewModal';

const DOC_CFG = {
  quotation: { label: 'QUOTATION', icon: 'fa-file-alt',           color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  invoice:   { label: 'INVOICE',   icon: 'fa-file-invoice-dollar', color: '#0f172a', bg: '#f8fafc', border: '#e2e8f0' },
};

const fmt = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function DocumentsList() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const initTab    = location.state?.tab || 'quotation';

  const [tab,      setTab]      = useState(initTab);
  const [docs,     setDocs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [viewing,  setViewing]  = useState(null);

  const load = useCallback(async (type) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/quotations?type=${type}`);
      setDocs(res.data.data || []);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const switchTab = (t) => { setTab(t); setSearch(''); };

  const handleEdit = (doc) => {
    navigate('/quotation', { state: { editDoc: doc } });
  };

  const handleDelete = async (doc) => {
    const res = await Swal.fire({
      title: `Delete ${doc.number}?`,
      text: 'This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Yes, delete',
    });
    if (!res.isConfirmed) return;
    try {
      await axiosInstance.delete(`/quotations/${doc.id}`);
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Deleted', showConfirmButton: false, timer: 1800 });
      load(tab);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: e?.response?.data?.error || e.message });
    }
  };

  const filtered = docs
    .filter(d => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (d.number || '').toLowerCase().includes(s) ||
             (d.customer_name || '').toLowerCase().includes(s);
    })
    .sort((a, b) => (b.sequence || 0) - (a.sequence || 0));

  const cfg = DOC_CFG[tab];

  /* ── Status badge for invoices ── */
  const statusBadge = (doc) => {
    if (doc.type !== 'invoice') return null;
    const balance = parseFloat(doc.balance || 0);
    if (balance <= 0) return { label: 'Paid', bg: '#dcfce7', color: '#166534' };
    if (parseFloat(doc.advance || 0) > 0) return { label: 'Partial', bg: '#fef9c3', color: '#92400e' };
    return { label: 'Unpaid', bg: '#fee2e2', color: '#991b1b' };
  };

  return (
    <div className="page-wrapper" style={{ background: '#f1f5f9' }}>
      <div className="content container-fluid" style={{ padding: '16px 20px' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg,#0a1f5c,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-folder-open" style={{ color: '#fff', fontSize: 18 }}></i>
            </div>
            <div>
              <h5 style={{ margin: 0, fontWeight: 800, color: '#0f172a', fontSize: 20 }}>Documents</h5>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>All saved quotations &amp; invoices</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/quotation')}
            style={{ background: 'linear-gradient(135deg,#0a1f5c,#2563eb)', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-plus"></i> Create New
          </button>
        </div>

        {/* ── Tabs + Search ── */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>

          <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 4px', gap: 0, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            {/* Tab buttons */}
            <div style={{ display: 'flex' }}>
              {Object.entries(DOC_CFG).map(([key, c]) => (
                <button key={key} onClick={() => switchTab(key)}
                  style={{
                    background: 'none', border: 'none', padding: '14px 22px', cursor: 'pointer',
                    fontWeight: tab === key ? 700 : 500, fontSize: 14,
                    color: tab === key ? '#0a1f5c' : '#64748b',
                    borderBottom: tab === key ? '2px solid #0a1f5c' : '2px solid transparent',
                    display: 'flex', alignItems: 'center', gap: 8, transition: 'all .2s',
                    whiteSpace: 'nowrap',
                  }}>
                  <i className={`fas ${c.icon}`}></i>
                  {c.label}
                  <span style={{
                    background: tab === key ? '#e0e7ff' : '#f1f5f9',
                    color: tab === key ? '#0a1f5c' : '#94a3b8',
                    borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 700,
                  }}>
                    {tab === key ? filtered.length : '—'}
                  </span>
                </button>
              ))}
            </div>

            {/* Search */}
            <div style={{ padding: '8px 16px' }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <i className="fas fa-search" style={{ position: 'absolute', left: 10, color: '#94a3b8', fontSize: 13 }}></i>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by number or customer…"
                  style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 11px 7px 30px', fontSize: 13, outline: 'none', width: 260 }}
                />
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 28, display: 'block', marginBottom: 12 }}></i>
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <i className="fas fa-inbox" style={{ fontSize: 40, display: 'block', marginBottom: 12, opacity: .3 }}></i>
              {search ? `No results for "${search}"` : `No saved ${cfg.label}s yet`}
              {!search && (
                <div>
                  <button onClick={() => navigate('/quotation')}
                    style={{ marginTop: 16, background: 'linear-gradient(135deg,#0a1f5c,#2563eb)', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    <i className="fas fa-plus me-1"></i>Create one now
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Document No.', 'Customer', 'Date', 'Amount', tab === 'invoice' ? 'Status' : '', 'Actions']
                      .filter(h => h !== '')
                      .map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .5, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(doc => {
                    const badge = statusBadge(doc);
                    return (
                      <tr key={doc.id}
                        style={{ borderBottom: '1px solid #f1f5f9', transition: 'background .15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                        {/* Document number */}
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: 6, padding: '3px 10px', fontSize: 13, fontWeight: 700 }}>
                            {doc.number}
                          </span>
                          {doc.revision > 0 && (
                            <span style={{ marginLeft: 6, background: '#fef9c3', color: '#92400e', border: '1px solid #fde68a', borderRadius: 20, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                              Rev {doc.revision}
                            </span>
                          )}
                        </td>

                        {/* Customer */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{doc.customer_name || '—'}</div>
                          {doc.customer_phone && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{doc.customer_phone}</div>}
                        </td>

                        {/* Date */}
                        <td style={{ padding: '14px 16px', color: '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                          {fmtDate(doc.date_str)}
                        </td>

                        {/* Amount */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{fmt(doc.subtotal)}</div>
                          {tab === 'invoice' && parseFloat(doc.advance || 0) > 0 && (
                            <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>Adv: {fmt(doc.advance)}</div>
                          )}
                        </td>

                        {/* Status (invoices only) */}
                        {tab === 'invoice' && (
                          <td style={{ padding: '14px 16px' }}>
                            {badge && (
                              <span style={{ background: badge.bg, color: badge.color, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                                {badge.label}
                              </span>
                            )}
                          </td>
                        )}

                        {/* Actions */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap' }}>
                            <button onClick={() => setViewing(doc)}
                              style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #86efac', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <i className="fas fa-eye me-1"></i>View
                            </button>
                            <button onClick={() => handleEdit(doc)}
                              style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <i className="fas fa-edit me-1"></i>Edit
                            </button>
                            <button onClick={() => handleDelete(doc)}
                              style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding: '12px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: 13, color: '#64748b' }}>
                {filtered.length} document{filtered.length !== 1 ? 's' : ''}
                {search && ` matching "${search}"`}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Doc View Modal ── */}
      {viewing && (
        <DocViewModal doc={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}
